/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Style, SEO and settings
import Helmet from 'react-helmet';
import { Col, css, Grid, Row, up, styled, withTheme } from '@smooth-ui/core-sc';

// Additional Components/Containers
import Button from '_platform/src/components/Button/Button';
import LoadAsync from '_platform/src/utils/LoadAsync';
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';

import { withSettings } from 'containers/WebApp/SettingsContext';
import bgImage1x from './images/Login-Background.jpg';
import bgImage2x from './images/Login-Background@2x.jpg';
import logo from '../../theme/logo-harman.svg';

const LoginProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginProvider" */ '_platform/src/containers/LoginProvider/LoginProvider'
  )
);
const LoginForm = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginForm" */ '_platform/src/components/LoginForm/LoginForm'
  )
);

const PageContainer = styled.div`
  align-items: center;
  background-color: #131d28;
  background-image: url(${bgImage1x});
  background-position: left top;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  @media only screen and (min-device-pixel-ratio: 1.5),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) {
    background-color: #131d28;
    background-image: url(${bgImage2x});
  }
`;

const LoginLogo = styled.h1`
  margin-bottom: 0;
  margin-top: 0;
  padding-bottom: 1rem;
  padding-top: 0;
  img {
    height: auto;
    width: 177px;
  }
`;

const IntroContainer = styled.div`
  border-radius: 10px;
  background-color: rgba(4, 23, 35, 0.62);
  box-sizing: border-box;
  color: #fff;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  padding: 2rem;
  h2 {
    font-size: 30px;
  }
  p {
    color: #fff;
    font-size: 30px;
    font-weight: bold;
    line-height: 1.2;
  }
  a {
    color: #fff;
    &:visited {
      color: #fff;
    }
  }

  a.register-button {
    color: #041723;
    font-weight: bold;
    border-radius: 5px;
  }
`;

const LoginContainer = styled.div`
  max-width: 500px;

  .reset-password {
    a {
      font-size: 14px;
      font-weight: normal;
    }
  }

  ${up(
    'lg',
    css`
      padding-left: 2rem;
      min-width: 300px;
    `
  )};
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>

    <PageContainer className="page-container">
      <Grid>
        <IntroContainer>
          <Grid>
            <Row>
              <Col xs={12} sm={10} osm={1} lg={6} olg={0}>
                <LoginLogo>
                  <img
                    src={logo}
                    alt={
                      settings.settingsApp && settings.settingsApp.siteName
                        ? settings.settingsApp.siteName
                        : 'ProgramName'
                    }
                  />
                </LoginLogo>
                <p className="spacer--bottom--small">
                  Welcome to Harman Rewards Rewarding you for your sales of
                  eligible Harman products over this festive period.
                </p>
                {/* <Button as={Link} className="register-button" to="/register/">
                  Register
                </Button> */}
              </Col>
              <Col xs={12} sm={10} osm={1} lg={6} olg={0}>
                <LoginContainer>
                  <h2 className="text--primary">Login</h2>
                  <LoginProvider disableMeta>
                    <LoginForm
                      formLabels={{ labelUserName: 'Email Address' }}
                    />
                  </LoginProvider>
                </LoginContainer>
              </Col>
            </Row>
          </Grid>
        </IntroContainer>
      </Grid>
    </PageContainer>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
